import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, doc, updateDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";

const HIDDEN_KEYS = new Set(["status", "submittedAt", "documentPaths"]);
const titleize = key => key.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase());

/**
 * Generic list+detail admin view for one Firestore submissions collection.
 * Reused across all 4 intake forms — they differ only in which fields exist
 * and which 1-2 fields best summarise a row, not in the review mechanics.
 */
export default function SubmissionsTab({ collectionName, label, summaryKeys, flashError }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, collectionName), orderBy("submittedAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [collectionName]);

  const toggleStatus = async item => {
    try {
      await updateDoc(doc(db, collectionName, item.id), { status: item.status === "reviewed" ? "new" : "reviewed" });
    } catch (e) {
      flashError?.(`Couldn't update status: ${e.message}`);
    }
  };

  const openDoc = async path => {
    try {
      const url = await getDownloadURL(ref(storage, path));
      window.open(url, "_blank", "noopener");
    } catch (e) {
      flashError?.(`Couldn't open document: ${e.message}`);
    }
  };

  const newCount = items.filter(i => i.status !== "reviewed").length;

  return (
    <div className="acard">
      <div className="atitle">
        <span>{label}</span>
        <span className="pi-meta">{items.length} total · {newCount} new</span>
      </div>

      {loading && <div className="empty-state">Loading…</div>}
      {!loading && items.length === 0 && <div className="empty-state">No submissions yet.</div>}

      {items.map(item => (
        <div key={item.id} className="pi-row" style={{ display: "block" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".75rem", flexWrap: "wrap" }}>
            <div>
              <div className="pi-title">{summaryKeys.map(k => item[k]).filter(Boolean).join(" — ") || "(no name)"}</div>
              <div className="pi-meta">
                {item.submittedAt?.toDate ? item.submittedAt.toDate().toLocaleString() : "—"}
                {" · "}
                <span className={`status-pill status-${item.status === "reviewed" ? "reviewed" : "new"}`}>{item.status === "reviewed" ? "reviewed" : "new"}</span>
              </div>
            </div>
            <div className="pi-acts">
              <button className="btn-gray" onClick={() => setOpenId(openId === item.id ? null : item.id)}>
                {openId === item.id ? "Hide" : "View"}
              </button>
              <button className="btn-g" onClick={() => toggleStatus(item)}>
                {item.status === "reviewed" ? "Mark New" : "Mark Reviewed"}
              </button>
            </div>
          </div>

          {openId === item.id && (
            <div className="sub-detail">
              <div className="sub-detail-grid">
                {Object.entries(item).filter(([k]) => k !== "id" && !HIDDEN_KEYS.has(k)).map(([k, v]) => (
                  <div key={k}>
                    <div className="sub-field-lbl">{titleize(k)}</div>
                    <div className="sub-field-val">{Array.isArray(v) ? (v.join(", ") || "—") : (v || "—")}</div>
                  </div>
                ))}
              </div>
              {item.documentPaths?.length > 0 && (
                <div className="sub-docs">
                  {item.documentPaths.map(p => (
                    <button key={p} className="sub-doc-link" onClick={() => openDoc(p)}>
                      📎 {p.split("/").pop().replace(/^\d+_/, "")}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
